<template lang="pug">
.dashboard-wrapper
  b-row
    b-col
      b-card(no-body, class="pt-2 px-2")
        filters(
          :customFiltersShow="true"
          :locationFilterShow="true"
          :dateRangeFilterShow="true"
          :physicalLocationFilterShow="true"
          :areaFilterShow="true"
          :locationDefaultValue="true"
          @changeFilter="updateFilter"
          @locations="updateLocations"
        )
        
  b-row(v-if="isLoadingMeetings || isLoadingCompliance")
    b-col
      b-card(class="py-5")
        loading(
          :active="true" 
          :is-full-page="false"
          :color="colors.primary"
        )

  div(v-else)
    //- Send report button
    div.d-flex.align-items-center.justify-content-end.mb-2
      span.mr-2
        | {{ sendingReportMessage }}
      b-button(variant="primary", :disabled="!!sendingReportMessage", @click="sendReportEmail")
        | {{ $t('message.sendReportToAreaLeader') }}

    //- Area/Physical Location Report
    div#report
      b-card(no-body)
        b-card-title(class="p-2 mb-0 text-center")
          | {{ $t('Compliance Percentage') }}

      b-row
        //- Daily Dialogue
        b-col(md="4")
          b-card(no-body)
            b-card-title(class="p-2 mb-0 text-center")
              | {{ $t('Daily Dialogue') }}

          b-card
            b-table-simple(hover, small, caption-top, responsive)
              b-thead(head-variant="light")
                b-tr
                  th.text-center {{ $t('Ranking') }}
                  th {{ $t('Department') }}
                  th.text-center {{ $t('Compliance') }}
                b-tr(v-for="(department, index) of departmentsByDailyDialogueCompliance", :key="index")
                  b-td.text-center {{ department.ranking }}
                  b-td {{ department.name }}
                  b-td.text-center.font-weight-bold(:class="getComplianceClass(department.compliance)") {{ department.compliance }}%

        //- Process Confirmation
        b-col(md="4")
          b-card(no-body)
            b-card-title(class="p-2 mb-0 text-center")
              | {{ $t('Processes Confirmation') }}

          b-card
            b-table-simple(hover, small, caption-top, responsive)
              b-thead(head-variant="light")
                b-tr
                  th.text-center {{ $t('Ranking') }}
                  th {{ $t('Department') }}
                  th.text-center {{ $t('Compliance') }}
                b-tr(v-for="(department, index) of departmentsByConfirmationCompliance", :key="index")
                  b-td.text-center {{ department.ranking }}
                  b-td {{ department.name }}
                  b-td.text-center.font-weight-bold(:class="getComplianceClass(department.compliance)") {{ department.compliance }}%

        //- Mood
        b-col(md="4")
          b-card(no-body)
            b-card-title(class="p-2 mb-0 text-center")
              | {{ $t('Mood') }}

          b-card
            b-table-simple(hover, small, caption-top, responsive)
              b-thead(head-variant="light")
                b-tr
                  th.text-center {{ $t('Ranking') }}
                  th {{ $t('Department') }}
                  th.text-center {{ $t('Compliance') }}
                b-tr(v-for="(department, index) of departmentsByMood", :key="index")
                  b-td.text-center {{ department.ranking }}
                  b-td {{ department.name }}
                  b-td.text-center.font-weight-bold(:class="getComplianceClass(department.mood)") {{ department.mood }}%
</template>

<script>
import { LOGO_CORREO } from '@/libs/emailAssets'
import { onMounted, ref } from "@vue/composition-api/dist/vue-composition-api"
import { BTable, BTableSimple, BThead, BTr, BTd, BCard, BCardTitle } from "bootstrap-vue"
import Filters from "@/views/organization/Filters.vue"
import i18n from '@/libs/i18n'
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import store from "@/store"
import useCommon from '@/views/organization/useCommon'
import useCommonDashboards from '@/views/habit/useCommonDashboards'
import { colors } from '@/constants'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import endpoints from '@/libs/endpoints'
import useNotifications from '@/composables/useNotifications'
import { get } from 'idb-keyval'
import html2canvas from 'html2canvas'
import useMongoDBFunctions from '@/views/habit/useMongoDBFunctions'

export default {
  components: {
    BTable,
    BTableSimple,
    BThead,
    BTr,
    BTd,
    BCard,
    BCardTitle,
    Filters,
    Loading,
  },
  setup() {
    const { handleError, getPhysicalLocationsForDropDown, physical_locations } = useCommon()
    const { getDatesFromRange, happinessIndex, dateDiffInDays, getLocationLabel } = useCommonDashboards()
    const { getItemsAggregate } = useLambda()
    const { confirmationsForWorkers } = useMongoDBFunctions()
    const { sendEmail } = useLambda()
    const { showSuccessMessage, showErrorMessage } = useNotifications()
    const userData = store.state?.userStore?.userData
    const userRole = userData.role
    const userId = userData.worker_id != null ? userData.worker_id.$oid : null
    const clientId = userData.role !== "admin" ? userData.client.$oid : null
    const userLocations = userData.locations?.length ? userData.locations : null
    const locationFilter = ref(userLocations ? userLocations.map(e => e.value) : [])
    const physicalLocationFilter = ref([])
    const { default_language, compliance_calculation_by_role } = JSON.parse(localStorage.getItem('clientData') || '{}')
    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()
    const lastDayOfMonthDate = new Date(currentYear, currentMonth + 1, 0)
    const lastDayOfMonthNumber = lastDayOfMonthDate.getDate()
    const dateRangeFilter = ref(`01-${currentMonth + 1}-${currentYear} to ${lastDayOfMonthNumber}-${currentMonth + 1}-${currentYear}`)
    const isLoadingMeetings = ref(true)
    const isLoadingCompliance = ref(true)
    const locations = ref([])
    get('locations').then(response => {
      if (response) locations.value = response
      else if (localStorage.locations) locations.value = JSON.parse(localStorage.locations)
    })
    const departmentsByDailyDialogueCompliance = ref([])
    const departmentsByConfirmationCompliance = ref([])
    const departmentsByMood = ref([])
    const sendingReportMessage = ref('')

    // Daily Dialogues and Mood
    async function listMeetings() {
      isLoadingMeetings.value = true
      const [startFilter, endFilter] = getDatesFromRange(dateRangeFilter.value)

      try {
        const initialQuery = {
          client_id: oid(clientId),
          deleted: { $ne: true },
          date: { $gte: date(startFilter), $lt: date(endFilter) },
        }

        const finalQuery = {}

        if (locationFilter.value.length > 0) {
          finalQuery['location._id'] = { $in: locationFilter.value }
        }

        if (physicalLocationFilter.value.length > 0) {
          finalQuery['physical_location._id'] = { $in: physicalLocationFilter.value }
        }

        const pipeline = [
          { $match: initialQuery },
          {
            $lookup: {
              from: 'location',
              localField: 'location',
              foreignField: '_id',
              pipeline: [
                { $project: { location: 1, dailyDialoguesCount: 1, dailyDialogueDays: 1, physical_location: 1 } },
                { $lookup: { from: 'physical_location', localField: 'physical_location', foreignField: '_id', as: 'physical_location', pipeline: [ { $project: { name: 1 } } ] } },
                { $addFields: { _id: { $toString: "$_id" }, physical_location: { $arrayElemAt: ["$physical_location.name", 0] } } }
              ],
              as: 'location'
            }
          },
          {
            $lookup: {
              from: 'physical_location',
              localField: 'physical_location',
              foreignField: '_id',
              pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }],
              as: 'physical_location'
            }
          },
          { $match: finalQuery },
          { $addFields: { _id: { $toString: "$_id" }, location: { $arrayElemAt: ["$location", 0] }, physical_location: { $arrayElemAt: ["$physical_location", 0] } } },
          { $sort: { date: 1 } },
        ]
      
        const items = await getItemsAggregate({ collection: 'meeting', pipeline })
        
        await parseMeetingData(items, startFilter, endFilter)
      } catch (error) {
        console.log(error);
        handleError({ error, defaultMessage: i18n.t('message.err_daily_dialogues_list') })
      } finally {
        isLoadingMeetings.value = false
      }
    }

    async function parseMeetingData(items, startFilter, endFilter) {
      const locationData = {}

      for (const meeting of items) {
        const location = meeting.location ? getLocationLabel(meeting.location) : undefined
        const locationId = meeting.location?._id

        if (location) {
          if (locationData.hasOwnProperty(location)) {
            locationData[location].dialogues++
            locationData[location].happy += meeting.happy
            locationData[location].neutral += meeting.neutral
            locationData[location].sad += meeting.sad
          } else {
            const dailyDialoguesCount = meeting.location.dailyDialoguesCount || 1
            const rawDays = meeting.location.dailyDialogueDays || [1, 2, 3, 4, 5, 6, 0]
            const locationOrderedDays = getOrderedDays(rawDays, default_language)

            // Calculation of days considered
            let qDays = 0
            if (endFilter < now) qDays = dateDiffInDays(startFilter, endFilter, locationOrderedDays)
            else if (startFilter < now) qDays = dateDiffInDays(startFilter, now, locationOrderedDays)

            locationData[location] = {
              dialogues: 1,
              happy: meeting.happy,
              neutral: meeting.neutral,
              sad: meeting.sad,
              id: locationId,
              expectedDialogues: qDays * dailyDialoguesCount,
            }
          }
        }
      }

      for (const location in locationData) {
        // Compliance calculation
        locationData[location].compliance = locationData[location].expectedDialogues === 0
          ? 0
          : Math.min(100, Math.round((locationData[location].dialogues / locationData[location].expectedDialogues) * 1000) / 10)

        // Mood calculation
        locationData[location].mood = happinessIndex(locationData[location].happy, locationData[location].neutral, locationData[location].sad)
      }

      departmentsByDailyDialogueCompliance.value = Object.entries(locationData)
        .sort((a, b) => b[1].compliance - a[1].compliance)
        .map(([key, value], index) => ({ name: key, ...value, ranking: `${index + 1}°` }))

      departmentsByMood.value = Object.entries(locationData)
        .sort((a, b) => b[1].mood - a[1].mood)
        .map(([key, value], index) => ({ name: key, ...value, ranking: `${index + 1}°` }))
    }

    function getOrderedDays(daysArray, language = 'es') {
      const order = language === 'es'
        ? [1, 2, 3, 4, 5, 6, 0] // L, M, X, J, V, S, D
        : [0, 1, 2, 3, 4, 5, 6] // S, M, T, W, Th, F, Sa

      return daysArray.slice().sort((a, b) => {
        return order.indexOf(a) - order.indexOf(b)
      })
    }

    // Compliance Default
    async function listEvents() {
      isLoadingCompliance.value = true
      const [startFilter, endFilter] = getDatesFromRange(dateRangeFilter.value)

      try {
        const initialQuery = {
          client_id: oid(clientId),
          'extendedProps.calendar': { $in: ["Pendientes", "Realizadas", "Vencidas"] },
          start: { $gte: date(startFilter), $lt: date(endFilter) },
          isBehaviourEvaluation: { $ne: true }
        }

        const finalQuery = {
          $or: [
            { "attendee.deleted": { $ne: true } },
            { "attendee.deleted": true, 'extendedProps.calendar': "Realizadas" },
            { attendee: null }
          ]
        }

        if (locationFilter.value.length > 0) {
          finalQuery.$or[0]['attendee.locations._id'] = { $in: locationFilter.value }
          finalQuery.$or[1]['attendee.locations._id'] = { $in: locationFilter.value }
          finalQuery.$or[2]['organizer.locations._id'] = { $in: locationFilter.value }
        }

        if (physicalLocationFilter.value.length > 0) {
          finalQuery.$or[0]['attendee.physical_locations._id'] = { $in: physicalLocationFilter.value }
          finalQuery.$or[1]['attendee.physical_locations._id'] = { $in: physicalLocationFilter.value }
          finalQuery.$or[2]['organizer.physical_locations._id'] = { $in: physicalLocationFilter.value }
          if (finalQuery.$or[3]) finalQuery.$or[3]['attendee.physical_locations._id'] = { $in: physicalLocationFilter.value }
        }

        const pipeline = [
          { $match: initialQuery },
          { $lookup: { from: 'worker', localField: 'attendee', foreignField: '_id', as: 'attendee', pipeline: [ { $project: { deleted: 1, name: 1, locations: 1, roles: 1, physical_locations: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }, { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1, zone: 1, agency: 1, physical_location: 1 } }, { $lookup: { from: 'physical_location', localField: 'physical_location', foreignField: '_id', as: 'physical_location', pipeline: [ { $project: { name: 1 } } ] } }, { $addFields: { _id: { $toString: "$_id" }, physical_location: { $arrayElemAt: ["$physical_location.name", 0] } } } ] } }, { $lookup: { from: 'physical_location', localField: 'physical_locations', foreignField: '_id',  as: 'physical_locations', pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } } ] } },
          { $lookup: { from: 'worker', localField: 'organizer', foreignField: '_id', as: 'organizer', pipeline: [ { $project: { name: 1, locations: 1, physical_locations: 1 } }, { $addFields: { _id: { $toString: "$_id" } } }, { $lookup: { from: 'location', localField: 'locations', foreignField: '_id', as: 'locations', pipeline: [ { $project: { location: 1, zone: 1, agency: 1, physical_location: 1 } }, { $lookup: { from: 'physical_location', localField: 'physical_location', foreignField: '_id', as: 'physical_location', pipeline: [ { $project: { name: 1 } } ] } }, { $addFields: { _id: { $toString: "$_id" }, physical_location: { $arrayElemAt: ["$physical_location.name", 0] } } } ] } }, { $lookup: { from: 'physical_location', localField: 'physical_locations', foreignField: '_id',  as: 'physical_locations', pipeline: [ { $project: { name: 1 } }, { $addFields: { _id: { $toString: "$_id" } } } ] } } ] } },          
          { $addFields: { attendee: { $arrayElemAt: ["$attendee", 0] }, organizer: { $arrayElemAt: ["$organizer", 0] } } },
          { $match: finalQuery },
        ]

        const items = await getItemsAggregate({ collection: 'event', pipeline })

        parseSupervisorData(items)
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.err_calendar_events_list') })
      } finally {
        isLoadingCompliance.value = false
      }
    }

    function parseSupervisorData(items) {
      const _locationCompliance = {};

      for (const element of items) {
        if (element.deleted) continue

        const status = element.extendedProps?.calendar;
        const eventLocations = element.attendee ? element.attendee.locations : element.organizer?.locations

        if (eventLocations === undefined) continue;

        for (const location of eventLocations) {
          if (locationFilter.value.length && !locationFilter.value.includes(location._id)) continue
          const locationLabel = getLocationLabel(location)
          if (!_locationCompliance[locationLabel]) {
            _locationCompliance[locationLabel] = {
              done: 0,
              pending: 0,
              overdue: 0,
              id: location._id
            };
          }

          if (status === "Realizadas") _locationCompliance[locationLabel]["done"] += 1;
          else if (status === "Pendientes") _locationCompliance[locationLabel]["pending"] += 1;
          else if (status === "Vencidas") _locationCompliance[locationLabel]["overdue"] += 1;
        }
      }

      // Set location compliance data
      for (const location in _locationCompliance) {
        _locationCompliance[location].total = _locationCompliance[location].done + _locationCompliance[location].pending + _locationCompliance[location].overdue
        _locationCompliance[location].compliance = _locationCompliance[location].total ? Math.round(_locationCompliance[location].done / _locationCompliance[location].total * 1000) / 10 : 0
      }

      departmentsByConfirmationCompliance.value = Object.entries(_locationCompliance)
        .sort((a, b) => b[1].compliance - a[1].compliance)
        .map(([key, value], index) => ({ name: key, ...value, ranking: `${index + 1}°` }))
    }

    // Compliance Fixed
    const listWorkers = async () => {
      const [startFilter, endFilter] = getDatesFromRange(dateRangeFilter.value)
      isLoadingCompliance.value = true

      const query = {
        limit: 50,
        total: true,
        offset: 0,
        userId: userId,
        privileges: userRole,
        client_id: clientId,
        date_gte: startFilter,
        date_lt: endFilter,
        locations: locationFilter.value,
        physical_locations: physicalLocationFilter.value,
        roles: [],
        processes: [],
        supervisors: [],
      }

      try {
        const items = await confirmationsForWorkers(query)
        const workersInside = items?.workers || []
        parseWorkersData(workersInside, startFilter, endFilter)
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.err_confirmation_list') })
      } finally {
        isLoadingCompliance.value = false
      }
    }

    const parseWorkersData = (workersInside, startFilter, endFilter) => {
      for(let element of workersInside){
        if (element.rolesData[0]?.numEventsRequired) {
          if (Math.floor(dateDiffInDays(startFilter, endFilter)) <= 31) element.meta = element.rolesData[0].numEventsRequired
          else if (Math.floor(dateDiffInDays(startFilter, endFilter)) <= 62 && Math.floor(dateDiffInDays(startFilter, endFilter)) > 31) element.meta = (element.rolesData[0].numEventsRequired) * 2
          else element.meta = (element.rolesData[0].numEventsRequired) * 3

          if (element.confirmationsCount <= 0){
            element.pending = Math.floor((dateDiffInDays(startFilter, endFilter) * parseInt(element.rolesData[0].numEventsRequired)) / 30)
          } else if (element.confirmationsCount >= Math.floor((dateDiffInDays(startFilter, endFilter) * parseInt(element.rolesData[0].numEventsRequired)) / 30) ){
            element.pending = 0
          } else {
            element.pending = Math.floor((dateDiffInDays(startFilter, endFilter) * parseInt(element.rolesData[0].numEventsRequired)) / 30) - element.confirmationsCount;
          }
        } else {
          element.meta = 0
          element.pending = 0
        }
        
        if (element.rolesData[0]?.numEventsRequired) {
          if (element.pending === 0) {
            element.compliance = Number((element.confirmationsCount / element.meta)*100).toFixed(1)
            if (element.compliance === "0.0") element.compliance = 0.0
            element.compliance = `${element.compliance}%`
          } else if (element.confirmationsCount <= 0) {
            element.compliance = `${0}%`
          } else {
            element.compliance = Number((element.confirmationsCount / element.meta)*100).toFixed(1)
            if (element.compliance === "100.0") element.compliance = 100
            element.compliance = `${element.compliance}%`
          }
        } else {
          if (element.pending === 0) {
            element.compliance = Number((element.confirmationsCount / 1)*100).toFixed(1)
            if (element.compliance === "0.0") element.compliance = 0.0
            element.compliance = `${element.compliance}%`
          } else if (element.confirmationsCount <= 0) {
            element.compliance = `${0}%`
          } else {
            element.compliance = Number((element.confirmationsCount / 1)*100).toFixed(1)
            if (element.compliance === "100.0") element.compliance = 100
            element.compliance = `${element.compliance}%`
          }
        }
      }

      const locationMap = {};

      for (const worker of workersInside) {
        for (const element of worker.locationsData) {
          if (locationFilter.value.length && !locationFilter.value.includes(element._id?.toString())) {
            continue
          }

          const name = getLocationLabel(element)

          if (!locationMap.hasOwnProperty(name)) {
            locationMap[name] = {
              totalDone: 0,
              pending: 0,
              meta: 0,
              id: element._id?.toString()
            }
          }

          locationMap[name].totalDone += worker.confirmationsCount
          locationMap[name].pending += worker.pending
          locationMap[name].meta += worker.meta
        }
      }

      for (const location in locationMap) {
        if (locationMap[location].meta) locationMap[location].compliance = Math.round(locationMap[location].totalDone / locationMap[location].meta * 1000) / 10
        else delete locationMap[location]
      }

      departmentsByConfirmationCompliance.value = Object.entries(locationMap)
        .sort((a, b) => b[1].compliance - a[1].compliance)
        .map(([key, value], index) => ({ name: key, ...value, ranking: `${index + 1}°` }))
    }

    const getComplianceClass = (value) => {
      if (value >= 90) return 'compliance-dark-green';
      if (value >= 70) return 'compliance-green';
      if (value >= 50) return 'compliance-yellow';
      if (value >= 25) return 'compliance-orange';
      return 'compliance-red';
    }

    // Capture and send report
    const getDepartmentsData = async () => {
      try {
        const query = {
          _id: { $in: locationFilter.value.map(e => oid(e)) },
        }

        const pipeline = [
          { $match: query },
          {
            $lookup: {
              from: 'area',
              localField: 'area',
              foreignField: '_id',
              pipeline: [
                {
                  $lookup: {
                    from: 'worker',
                    localField: 'areaLeads',
                    foreignField: '_id',
                    pipeline: [
                      { $project: { name: 1, email: 1 } },
                      { $addFields: { _id: { $toString: "$_id" } } }
                    ],
                    as: 'areaLeads'
                  }
                },
                {
                  $project: {
                    name: 1,
                    areaLeads: 1
                  }
                },
                { $addFields: { _id: { $toString: "$_id" } } }
              ],
              as: 'area'
            }
          },
          { $addFields: { _id: { $toString: "$_id" }, area: { $arrayElemAt: ["$area", 0] } } },
        ]
      
        const items = await getItemsAggregate({ collection: 'location', pipeline })
        return items
      } catch (error) {
        console.log(error);
        handleError({ error, defaultMessage: i18n.t('message.location_fetch_error') })
      }
    }

    const captureReportImage = async () => {
      const reportElement = document.querySelector('#report')
      if (!reportElement) return null

      const canvas = await html2canvas(reportElement, { backgroundColor: '#f5f5f5' })
      const dataUrl = canvas.toDataURL('image/png')
      return dataUrl
    }

    const sendReportEmail = async () => {
      try {
        if (!locationFilter.value.length) {
          handleError({ defaultMessage: i18n.t('message.mustSelectOneDepartment') })
          return
        }

        sendingReportMessage.value = i18n.t('message.sendingReport')

        const departmentsData = await getDepartmentsData()
        let physicalLocationsNames = ''
        if (physicalLocationFilter.value.length && physical_locations.value.length) {
          const selectedNames = physical_locations.value
            .filter(loc => physicalLocationFilter.value.includes(loc.value))
            .map(loc => loc.title)
          physicalLocationsNames = selectedNames.length ? selectedNames.join(' / ') : ''
        }

        if (!departmentsData?.length) {
          handleError({ defaultMessage: i18n.t('message.noDepartmentDataFound') })
          return
        }

        const areaLeads = departmentsData.reduce((acc, location) => {
          if (location.area?.areaLeads?.length) {
            location.area.areaLeads.forEach(lead => {
              if (lead.email && !acc.includes(lead.email)) {
                acc.push(lead.email)
              }
            })
          }
          return acc
        }, [])

        if (!areaLeads.length) {
          handleError({ defaultMessage: i18n.t('message.noAreaLeadsFound') })
          return
        }

        const imageDataUrl = await captureReportImage()
        if (!imageDataUrl) return

        const base64Image = imageDataUrl.replace(/^data:image\/png;base64,/, "")

        // La Lambda arma el MIME con { filename, data, type }: los campos en mayúsculas eran
        // los del RawMessage que armaba este componente con la SES directa.
        const attachment = {
          filename: `${i18n.t('Area/Physical Location')}.png`,
          data: base64Image,
          type: 'image/png',
        }

        const departments = departmentsData.map(e => e.location).join(' / ')
        const subject = `${i18n.t('Area/Physical Location')}: ${departments}`
        const body = getBodyTemplate(departments, physicalLocationsNames)

        await sendEmail({ to: areaLeads, subject, htmlBody: body, attachment })
        showSuccessMessage(i18n.tc('message.numberOfEmailsSent', 0, { count: areaLeads.length }))
      } catch (error) {
        console.log(error)
        handleError({ error, defaultMessage: i18n.t('message.errorSendingEmail') })
      } finally {
        sendingReportMessage.value = ''
      }
    }

    const getBodyTemplate = (departments, physicalLocationsNames) => {
      return `
        <html>
          <table style="max-width: 600px; padding: 10px; margin:0 auto; border-collapse: collapse; border-radius: 1em; overflow: hidden;">
            <tr>
              <td style="background-color: #ecf0f1; text-align: center; padding: 0">
                <img width="20%" style="margin: 2% 0% 1%" src="${LOGO_CORREO}" alt="Habit Logo">
              </td>
            </tr>
            
            <tr>
              <td style="background-color: #ecf0f1">
                <div style="color: #34495e; margin: 2% 10%; font-family: sans-serif">
                  <p style="margin: 2px; font-size: 15px">
                    ${i18n.t('message.attachedIsTheDepartmentReport')}: ${departments}
                  </p>
                  ${ physicalLocationsNames.length ? `<p style="margin: 2px; font-size: 15px">
                    ${i18n.t('label.Physical Locations')}: ${physicalLocationsNames}
                  </p>` : ''}
                  <p style="margin: 2px; font-size: 15px">
                    ${i18n.t('message.pressButtonToSeeInHabit')}:
                  </p>

                  <div style="width: 100%; text-align: center; margin: 7% 0%;">
                    <a style="text-decoration: none; border-radius: 5px; padding: 11px 23px; color: white; background-color: #4e8769; font-size: 17px" href="${endpoints.app_url}/habit/area-physical_location/report">
                      ${i18n.t('message.see_report_in_habit')}
                    </a>
                  </div>
                </div>
              </td>
            </tr>
          </table>
        </html>
      `
    }

    function updateFilter(data) {
      locationFilter.value = data.locationFilter
      physicalLocationFilter.value = data.physicalLocationFilter
      dateRangeFilter.value = data.dateRangeFilter

      listMeetings()

      if (compliance_calculation_by_role) listWorkers()
      else listEvents()
    }

    function updateLocations(data) {
      if (!locations.value.length) {
        locations.value = data;
        listMeetings();
      }
    }

    onMounted(() => {
      listMeetings()
      getPhysicalLocationsForDropDown()

      if (compliance_calculation_by_role) listWorkers()
      else listEvents()
    })

    return {
      updateFilter,
      updateLocations,
      isLoadingMeetings,
      isLoadingCompliance,
      colors,
      departmentsByDailyDialogueCompliance,
      departmentsByConfirmationCompliance,
      departmentsByMood,
      getComplianceClass,
      sendReportEmail,
      sendingReportMessage,
    };
  },
};
</script>

<style scoped>
.compliance-dark-green {
  background-color: #006400;
  color: white;
}

.compliance-green {
  background-color: #00cc44;
  color: white;
}

.compliance-yellow {
  background-color: #ffcc00;
  color: black;
}

.compliance-orange {
  background-color: #ff8000;
  color: white;
}

.compliance-red {
  background-color: #cc0000;
  color: white;
}
</style>